import React, { useState } from "react";
import { Package, Layers } from "lucide-react";
import BlockRenderer from "../components/BlockRenderer";
import ItemCard from "../components/ItemCard";
import Lightbox from "../components/Lightbox";
import SmartImage from "../components/SmartImage";
import HubGrid from "../components/HubGrid";
import {
  BrandPlate,
  CrumbLine,
  CtaPair,
  FramedShot,
  HeroBand,
  SectionHead,
  SiblingPills,
  StatChip,
  useEnquiry,
} from "./parts";

/**
 * BRAND / RANGE page.
 * Split hero: brand mark + copy on the left, framed hero photograph on the
 * right. Then the product range grid, then the brand story with its gallery.
 */
export default function RangeTemplate({
  page,
  items = [],
  childPages: kids = [],
  siblings = [],
  breadcrumbs = [],
  parentLabel,
  phone,
}) {
  const [lb, setLb] = useState(null);
  const heroImg = page.hero_image || page.thumb_image;
  const to = useEnquiry(page, heroImg, parentLabel);

  const blocks = page.blocks || [];
  const textBlocks = blocks.filter((b) => b.type !== "image");
  const imageBlocks = blocks.filter((b) => b.type === "image");
  const strip = imageBlocks.filter((b) => b.url !== heroImg).slice(0, 8);
  const galleryUrls = strip.map((b) => b.url);
  const heading = page.h1 && page.h1.length < 90 ? page.h1 : page.title;

  return (
    <div data-testid="template-range">
      <HeroBand image={heroImg} tall dim={0.18}>
        <div className="grid items-center gap-10 lg:grid-cols-12">
          <div className="lg:col-span-6">
            <CrumbLine breadcrumbs={breadcrumbs} parentLabel={parentLabel} />
            {page.brand_logo && <BrandPlate src={page.brand_logo} alt={page.title} className="mt-4" />}
            <h1
              className="mt-5 text-3xl font-bold leading-tight text-[color:var(--nq-sand)] md:text-5xl"
              data-testid="page-title"
            >
              {heading}
            </h1>
            {page.meta_description && (
              <p className="mt-4 max-w-xl text-sm leading-relaxed text-[color:var(--nq-stone)] md:text-base">
                {page.meta_description}
              </p>
            )}

            {(items.length > 0 || kids.length > 0) && (
              <div className="mt-6 flex flex-wrap gap-3">
                {items.length > 0 && <StatChip value={items.length} label={items.length === 1 ? "Product" : "Products"} />}
                {kids.length > 0 && <StatChip value={kids.length} label="Sub-categories" />}
              </div>
            )}

            <div className="mt-7">
              <CtaPair to={to} label={`Enquire about ${page.title.slice(0, 26)}`} phone={phone} />
            </div>
          </div>

          <div className="lg:col-span-6">
            {heroImg ? (
              <FramedShot src={heroImg} alt={heading} ratio="aspect-[4/3]" fit="cover" />
            ) : (
              <FramedShot src={null} alt={heading} />
            )}
          </div>
        </div>
      </HeroBand>

      <div className="container-nq section-nq space-y-16">
        {items.length > 0 && (
          <section>
            <SectionHead
              eyebrow="The range"
              title={`${page.title} we supply`}
              sub="Every item is fitted and matched to your vehicle. Tap enquire for pricing and fitment."
              right={
                <div className="flex items-center gap-2 text-xs text-[color:var(--nq-stone)]">
                  <Package className="h-4 w-4 gold-text" />
                  {items.length} listed
                </div>
              }
            />
            {items.length === 1 ? (
              <div className="grid gap-6 lg:grid-cols-2" data-testid="page-items-grid">
                <ItemCard item={items[0]} pageTitle={page.title} />
                <div className="flex flex-col justify-center">
                  <h3 className="text-xl font-semibold text-[color:var(--nq-sand)] md:text-2xl">
                    {items[0].name}
                  </h3>
                  {items[0].description && (
                    <p className="mt-3 text-sm leading-relaxed text-[color:var(--nq-stone)]">
                      {items[0].description.slice(0, 420)}
                    </p>
                  )}
                  <div className="mt-5">
                    <CtaPair
                      to={to}
                      label="Enquire about this product"
                      phone={phone}
                      testid="solo-item-enquire-button"
                      compact
                    />
                  </div>
                </div>
              </div>
            ) : (
              <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3" data-testid="page-items-grid">
                {items.map((it) => (
                  <ItemCard key={it.id} item={it} pageTitle={page.title} />
                ))}
              </div>
            )}
          </section>
        )}

        {kids.length > 0 && (
          <section>
            <SectionHead
              eyebrow="Browse deeper"
              title={`${page.title} categories`}
              right={
                <div className="flex items-center gap-2 text-xs text-[color:var(--nq-stone)]">
                  <Layers className="h-4 w-4 gold-text" />
                  {kids.length} sections
                </div>
              }
            />
            <HubGrid items={kids} parentLabel={page.title} />
          </section>
        )}

        {textBlocks.length > 0 && (
          <section className="grid gap-10 lg:grid-cols-12">
            <div className="lg:col-span-7">
              <SectionHead eyebrow="About the brand" title={`Why ${page.title}`} />
              <BlockRenderer blocks={textBlocks} />
            </div>
            {strip.length > 0 && (
              <div className="lg:col-span-5">
                <div className="grid grid-cols-2 gap-3" data-testid="range-gallery-strip">
                  {strip.map((im, i) => (
                    <button
                      key={i}
                      type="button"
                      onClick={() => setLb(i)}
                      className="card-nq cursor-zoom-in focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[color:var(--nq-gold)]"
                      data-testid="range-gallery-item"
                    >
                      <SmartImage src={im.url} alt={im.alt} ratio="aspect-square" fit="cover" />
                    </button>
                  ))}
                </div>
              </div>
            )}
          </section>
        )}

        <SiblingPills siblings={siblings} />
      </div>

      <Lightbox images={galleryUrls} index={lb} onClose={() => setLb(null)} onIndex={setLb} />
    </div>
  );
}
